/**
 * Reset database — the presenter's one-button reseed.
 *
 * Replaces the tenant's patients, visits, shifts, blocks and availability with a
 * fresh seed for the chosen demo day; the org, its users and their access stay.
 * Visit statuses are as of the demo time (Pete, 2026-10-13), so a reset in the
 * morning for an afternoon demo shows the morning's visits done; it offers now
 * on today and 08:00 on a later day, for a rehearsal. Weekends cannot be
 * picked: the seed is for working days.
 *
 * Dana's patients in the seed carry the number of the handset in the room, so the
 * demo's texts reach whoever presents (Pete, 2026-10-12); everybody else gets a
 * placeholder, so a move across the staff does not burst it. The number is required;
 * the service refuses one that is not on its SMS allow-list, and the field says
 * so. This browser remembers the last number that reset, as a convenience only.
 *
 * A reset that lands switches this browser's provider context to Dana, the way
 * Switch provider in the user menu would (Pete, 2026-10-12): the demo is shown as
 * her, and a fresh seed is where every run starts. Only for a user who could
 * switch provider by hand; other browsers that follow the reset keep theirs.
 */
import { useState } from "react";
import { Button, Stack, TextField } from "@mui/material";
import { format, isWeekend } from "date-fns";
import { TimePicker } from "@mui/x-date-pickers";
import DatePicker from "~/components/common/forms/DatePicker";
import { Permission, useHasPermission } from "~/hooks/permissions";
import { useOrganization } from "~/providers/GraphqlProvider";
import { toDateKey } from "@caredna-marlin/utils/homeHealthDemo/engine/clock";
import type { DateKey } from "@caredna-marlin/utils/homeHealthDemo/state";
import { defaultDemoTime, defaultResetDay } from "../engine/tenantDemoDay";
import * as actions from "./actions";
import { HelpTip } from "./HelpTip";

/** A date key as the picker's local calendar date, and back. */
const keyToLocalDate = (key: DateKey): Date => {
  const [y, m, d] = key.split("-").map(Number);
  return new Date(y, m - 1, d);
};
const localDateToKey = (date: Date): DateKey => format(date, "yyyy-MM-dd");
/** A branch-zone `HH:mm` as the picker's local time of day, and back. */
const hhmmToLocalDate = (hhmm: string): Date => {
  const [h, m] = hhmm.split(":").map(Number);
  return new Date(2000, 0, 1, h, m);
};

const PHONE_KEY = "home-health-demo:reset-phone";

/** A North American number's ten digits, or undefined. The service reads it the same way. */
export function tenDigits(raw: string): string | undefined {
  const digits = raw.replace(/\D/g, "");
  if (digits.length === 10) return digits;
  if (digits.length === 11 && digits.startsWith("1")) return digits.slice(1);
  return undefined;
}

function rememberedPhone(): string {
  try {
    return localStorage.getItem(PHONE_KEY) ?? "";
  } catch {
    return "";
  }
}

function rememberPhone(phone: string): void {
  try {
    localStorage.setItem(PHONE_KEY, phone);
  } catch {
    // a private window or blocked storage: the field simply starts empty next time
  }
}

export function ResetDemoControl({ timeZone, disabled }: { timeZone: string; disabled?: boolean }) {
  const canReset = useHasPermission(Permission.copyDemoData) === true;
  const canSwitchProvider = useHasPermission(Permission.impersonateAnyProvider) === true;
  const { overrideProviderId } = useOrganization();
  const [day, setDay] = useState<DateKey>(() => defaultResetDay(new Date(), timeZone));
  /** The hour the demo will run, once the presenter sets it; until then the default for the day. */
  const [time, setTime] = useState<string>();
  const [phone, setPhone] = useState(rememberedPhone);
  const [confirm, setConfirm] = useState(false);
  const [resetting, setResetting] = useState(false);
  /** The service's refusal of this number, shown on the field until the number changes. */
  const [refused, setRefused] = useState<string>();
  const today = toDateKey(new Date(), timeZone);
  const digits = tenDigits(phone);
  const demoTime = time ?? defaultDemoTime(day, new Date(), timeZone);

  const onClick = async () => {
    if (!digits) return;
    if (!confirm) {
      setConfirm(true);
      return;
    }
    setConfirm(false);
    setResetting(true);
    try {
      await actions.resetDemo(day, demoTime, digits);
      rememberPhone(phone);
      const dana = actions.boundProviderId("dana");
      if (canSwitchProvider && dana !== undefined) overrideProviderId?.(dana);
    } catch (error) {
      // Logged by the action; an unlisted number is also the field's to say.
      if (error instanceof Error && /allow-list/.test(error.message)) setRefused("Not on the SMS allow-list; engineering adds numbers");
    } finally {
      setResetting(false);
    }
  };

  return (
    <Stack spacing={1}>
      <TextField
        label="Demo SMS Phone #"
        size="small"
        type="tel"
        value={phone}
        onChange={(e) => {
          setPhone(e.target.value);
          setRefused(undefined);
        }}
        disabled={resetting}
        error={(phone !== "" && !digits) || refused !== undefined}
        helperText={phone !== "" && !digits ? "Ten digits" : refused}
        sx={{ width: 220 }}
      />
      <Stack direction="row" spacing={1} alignItems="center">
        <DatePicker
          label="Demo day"
          value={keyToLocalDate(day)}
          onChange={(d) => d && setDay(localDateToKey(d))}
          minDate={keyToLocalDate(today)}
          shouldDisableDate={isWeekend}
          disabled={resetting}
          slots={{ textField: TextField }}
          slotProps={{ textField: { size: "small", sx: { width: 160 } } }}
        />
        <TimePicker
          label="Demo time"
          value={hhmmToLocalDate(demoTime)}
          onChange={(d) => d && !Number.isNaN(d.getTime()) && setTime(format(d, "HH:mm"))}
          disabled={resetting}
          slotProps={{ textField: { size: "small", sx: { width: 140 } } }}
        />
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center">
        <Button
          size="small"
          variant={confirm ? "contained" : "outlined"}
          color="warning"
          disabled={disabled || resetting || !canReset || !digits || refused !== undefined}
          onClick={onClick}
          onBlur={() => setConfirm(false)}
        >
          {resetting ? "Resetting…" : confirm ? "Confirm reset" : "Reset database"}
        </Button>
        <HelpTip
          label="Reset database"
          text={
            canReset
              ? "For everyone: puts patients, visits, shifts and blocks back to a fresh seed for this day, and every open browser follows within a minute. Users and their access stay, and this browser switches to Dana. Set Demo time to when the demo will run: today's visits before it read done, in progress or en route. Dana's patients get the handset's number, so the texts about her visits reach it; the number has to be on the environment's SMS allow-list. Use this between runs."
              : "Reset database needs the copyDemoData permission."
          }
        />
      </Stack>
    </Stack>
  );
}
